import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { newId } from "@/lib/resumeUtils";
import WizardModal from "./WizardModal";
import ThinkingLoader from "@/components/ThinkingLoader";
import YmPicker from "./YmPicker";
import { Sparkles, RefreshCw, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ExperienceWizard({ mode = "add", initial, onClose, onSave }) {
  const { t, lang } = useT();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(initial?.role || "");
  const [company, setCompany] = useState(initial?.company || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [start, setStart] = useState(initial?.start || "");
  const [end, setEnd] = useState(initial?.end || "");
  const [current, setCurrent] = useState(initial?.current || false);
  const [what, setWhat] = useState(initial?.description || "");
  const [proposal, setProposal] = useState("");
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/resumes/ai/experience", {
        role, company, start, end, current, raw_input: what, lang,
      });
      setProposal(data.description);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("wizFailed"));
    } finally {
      setGenerating(false);
    }
  }, [role, company, start, end, current, what, lang, t]);

  useEffect(() => {
    if (step === 3 && !proposal && !generating) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const next = () => {
    if (step === 1) {
      if (!role.trim() || !company.trim()) { toast.error(t("wizExpRequired")); return; }
      setStep(2);
    } else if (step === 2) {
      if (!what.trim()) { toast.error(t("wizWhatRequired")); return; }
      setStep(3);
    }
  };

  const save = () => {
    onSave({
      id: initial?.id || newId(),
      role: role.trim(), company: company.trim(), location: location.trim(),
      start, end: current ? "" : end, current,
      description: proposal.trim(),
    });
  };

  return (
    <WizardModal
      testId="exp-wizard"
      title={mode === "optimize" ? t("wizExpTitleOptimize") : t("wizExpTitleAdd")}
      onClose={onClose}
    >
      <p className="font-serif text-xl italic text-neutral-800 mb-5">
        {step === 1 ? t("wizStep1") : step === 2 ? t("wizStep2") : t("wizStep3")}
      </p>

      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="ed-label">{t("rbRole")} *</label>
              <input data-testid="exp-wizard-role" className="ed-input" value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            <div>
              <label className="ed-label">{t("rbCompany")} *</label>
              <input data-testid="exp-wizard-company" className="ed-input" value={company} onChange={(e) => setCompany(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="ed-label">{t("rbFrom")}</label>
              <YmPicker testId="exp-wizard-start" value={start} onChange={setStart} />
            </div>
            <div>
              <label className="ed-label">{t("rbUntil")}</label>
              <YmPicker testId="exp-wizard-end" value={end} onChange={setEnd} disabled={current} />
              <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  data-testid="exp-wizard-current"
                  type="checkbox"
                  checked={current}
                  onChange={(e) => setCurrent(e.target.checked)}
                  className="w-4 h-4 accent-neutral-900"
                />
                <span className="text-xs font-mono text-neutral-600">{t("rbCurrentRole")}</span>
              </label>
            </div>
          </div>
          <div>
            <label className="ed-label">{t("rbLocation")}</label>
            <input data-testid="exp-wizard-location" className="ed-input" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <label className="ed-label">{t("wizWhatLabel")}</label>
          <textarea
            data-testid="exp-wizard-what"
            className="ed-input min-h-[10rem]"
            placeholder={t("wizWhatPh")}
            value={what}
            onChange={(e) => setWhat(e.target.value)}
          />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {generating ? (
            <div className="ed-card p-8 text-center">
              <Sparkles size={20} strokeWidth={1.5} className="mx-auto text-neutral-400 animate-pulse" />
              <p className="ed-eyebrow mt-3" data-testid="exp-wizard-generating">{t("wizGenerating")}</p>
              <div className="mt-2 flex justify-center">
                <ThinkingLoader stage="resume" testId="exp-wizard-thinking" />
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-mono text-neutral-500">{t("wizProposalHint")}</p>
              <textarea
                data-testid="exp-wizard-proposal"
                className="ed-input min-h-[12rem]"
                value={proposal}
                onChange={(e) => setProposal(e.target.value)}
              />
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-200">
        {step > 1 ? (
          <button data-testid="exp-wizard-back" className="ed-btn ed-btn-outline" onClick={() => setStep(step - 1)} disabled={generating}>
            <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
          </button>
        ) : <span />}
        {step < 3 ? (
          <button data-testid="exp-wizard-next" className="ed-btn" onClick={next}>
            {t("wizNext")} <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <div className="flex gap-2">
            <button data-testid="exp-wizard-regenerate" className="ed-btn ed-btn-outline" onClick={generate} disabled={generating}>
              <RefreshCw size={14} strokeWidth={1.5} /> {t("wizRegenerate")}
            </button>
            <button data-testid="exp-wizard-save" className="ed-btn" onClick={save} disabled={generating || !proposal.trim()}>
              <Check size={14} strokeWidth={1.5} /> {t("wizKeepSave")}
            </button>
          </div>
        )}
      </div>
    </WizardModal>
  );
}
